import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Target, FlaskConical, Headphones, ArrowRight } from "lucide-react";

const IMG = "https://images.unsplash.com/photo-1782094673136-5198a372980c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxzZXJ2ZXIlMjByb29tJTIwY29vbGluZyUyMGZhbnN8ZW58MHx8fHwxNzgyNzU3NTAzfDA&ixlib=rb-4.1.0&q=85";

const values = [
  { icon: FlaskConical, title: "Engineered, not guessed", desc: "Every shroud is validated with thermal and acoustic testing on real Antminer hardware before it ships." },
  { icon: Target, title: "Miner-first design", desc: "We mine ourselves. Each product solves a problem we hit running rigs at home and in farms." },
  { icon: Headphones, title: "Real human support", desc: "Questions about fitment or overclocking? Talk to people who actually run the gear." },
];

const About = () => (
  <div data-testid="about-page">
    <section className="relative overflow-hidden border-b border-[#27272A]">
      <img src={IMG} alt="Mining hardware" className="absolute inset-0 h-full w-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-[#09090B]/70" />
      <div className="relative mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">About AsicMod</span>
          <h1 className="mt-4 font-heading text-4xl font-semibold tracking-tighter text-[#FAFAFA] sm:text-5xl">
            We make mining liveable.
          </h1>
          <p className="mt-5 text-base leading-relaxed text-[#A1A1AA]">
            AsicMod started in a garage full of screaming Antminers. We loved mining — but
            not the 80dB jet-engine roar. So we designed a better shroud. Today we build
            precision PETG cooling parts trusted by home miners and farm operators worldwide.
          </p>
        </motion.div>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
      <div className="grid gap-5 md:grid-cols-3">
        {values.map((v, i) => (
          <motion.div
            key={v.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            data-testid={`value-${i}`}
            className="rounded-sm border border-[#27272A] bg-[#0E0E11] p-8 transition-colors hover:border-[#EA580C]"
          >
            <v.icon size={26} className="text-primary" />
            <h3 className="mt-5 font-heading text-xl font-medium text-[#FAFAFA]">{v.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#A1A1AA]">{v.desc}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-16 grid gap-px overflow-hidden rounded-sm border border-[#27272A] bg-[#27272A] sm:grid-cols-3">
        {[
          { n: "12,000+", l: "Shrouds shipped" },
          { n: "60 dB", l: "Average noise level" },
          { n: "40+", l: "Countries served" },
        ].map((s) => (
          <div key={s.l} className="bg-[#0E0E11] p-10 text-center">
            <div className="font-mono text-4xl font-semibold text-primary">{s.n}</div>
            <div className="mt-2 text-sm text-[#A1A1AA]">{s.l}</div>
          </div>
        ))}
      </div>

      <div className="mt-16 flex flex-col items-center rounded-sm border border-[#27272A] bg-[#0E0E11] p-12 text-center">
        <h2 className="font-heading text-2xl font-medium text-[#FAFAFA] sm:text-3xl">Ready to quiet down your rig?</h2>
        <Link to="/product/asic-fan-shroud" data-testid="about-cta" className="group mt-6 inline-flex items-center gap-2 rounded-sm bg-primary px-7 py-3.5 text-sm font-semibold text-[#09090B] transition-all hover:bg-[#EA580C] hover:shadow-[0_0_20px_rgba(249,115,22,0.4)]">
          Shop the shroud <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </section>
  </div>
);

export default About;
